import { inject, Injectable } from "@angular/core";
import { documentId, Firestore } from "@angular/fire/firestore";
import {
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  DocumentReference,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from "firebase/firestore";
import { cloudFunctionNames, IGroup, IHttpsCallableResponse, UUID } from "src/app/core/interfaces";
import { IMirrorEntityToGroupReq } from "@cloud-functions/interfaces";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { chunkArray }  from "@cloud-functions/utils";

interface IGroupService {
  getGroup(orgId: UUID, groupId: UUID): Promise<IGroup>;
  getGroupsByIds(orgId: UUID, groupIds: Set<UUID>): Promise<IGroup[]>
  // entity-related methods
  moveEntityToGroup(orgId: UUID, entityId: UUID, entityLegalName: string, fromGroupId: UUID, toGroupId: UUID): Promise<[IGroup, IGroup]>;
  addEntityToGroup(orgId: UUID, entityId: UUID, entityLegalName: string, groupId: UUID): Promise<IGroup>;
  removeEntityFromGroup(orgId: UUID, entityId: UUID, entityLegalName: string, groupId: UUID): Promise<IGroup>;
  mirrorEntityToGroup(entityId: UUID, entityLegalName: string, orgId: UUID, groupId: UUID): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class GroupService implements IGroupService {
  private firestore: Firestore = inject(Firestore);
  private functions: Functions = inject(Functions);

  private getGroupCollectionRef(orgId: UUID): CollectionReference<IGroup> {
    return collection(
      this.firestore,
      `organization/${orgId}/groups`
    ) as CollectionReference<IGroup>;
  }

  private getGroupRef(orgId: UUID, groupId: UUID): DocumentReference<IGroup> {
    const groupCollectionRef = this.getGroupCollectionRef(orgId);

    return doc(
      groupCollectionRef,
      groupId
    );
  }

  public async getGroup(orgId: UUID, groupId: UUID): Promise<IGroup> {
    const groupRef = this.getGroupRef(orgId, groupId);
    const groupSnap = await getDoc(groupRef);

    if (!groupSnap.exists()) {
      throw new Error("Group not found");
    }

    return {
      ...groupSnap.data(),
      id: groupSnap.id
    };
  }

  public async getGroupsByIds(orgId: UUID, groupIds: Set<UUID>): Promise<IGroup[]> {
    const groupsCollectionRef = this.getGroupCollectionRef(orgId);

    const groupIdChunks = chunkArray(Array.from(groupIds), 10);

    const groupsSnap = await Promise.all(
      groupIdChunks.map(chunk =>
        getDocs(
          query(
            groupsCollectionRef,
            where(documentId(), "in", chunk)
          )
        )
      )
    );

    return groupsSnap.flatMap(snapshot =>
      snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      } as IGroup))
    );
  }

  public moveEntityToGroup(
    orgId: UUID,
    entityId: UUID,
    entityLegalName: string,
    fromGroupId: UUID,
    toGroupId: UUID
  ): Promise<[IGroup, IGroup]> {
    return Promise.all([
      this.removeEntityFromGroup(orgId, entityId, entityLegalName, fromGroupId),
      this.addEntityToGroup(orgId, entityId, entityLegalName, toGroupId)
    ])
  }

  public async addEntityToGroup(
    orgId: UUID,
    entityId: UUID,
    entityLegalName: string,
    groupId: UUID
  ): Promise<IGroup> {
    const groupRef = this.getGroupRef(orgId, groupId);
    await updateDoc(groupRef, {
      entities: arrayUnion({
        id: entityId,
        name: entityLegalName
      })
    });

    return this.getGroup(orgId, groupId);
  }

  public async removeEntityFromGroup(
    orgId: UUID,
    entityId: UUID,
    entityLegalName: string,
    groupId: UUID
  ): Promise<IGroup> {
    const groupRef = this.getGroupRef(orgId, groupId);
    await updateDoc(groupRef, {
      entities: arrayRemove({
        id: entityId,
        name: entityLegalName
      })
    });

    return this.getGroup(orgId, groupId);
  }

  public async mirrorEntityToGroup(
    entityId: UUID,
    entityLegalName: string,
    orgId: UUID,
    groupId: UUID
  ): Promise<void> {
    const mirrorEntityToGroupCallable = httpsCallable<
      IMirrorEntityToGroupReq,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.entity.mirrorEntityToGroup);

    const mirrorEntityToGroupResponse = await mirrorEntityToGroupCallable({
      entityId,
      entityLegalName,
      orgId,
      groupId
    });

    if (!mirrorEntityToGroupResponse.data.success) {
      throw new Error(mirrorEntityToGroupResponse.data.message);
    }
  }
}
